import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Star, ChevronDown, ChevronUp, Video, FileText, Mic } from "lucide-react";
import ImageUploader from "./ImageUploader";

/**
 * ReviewComposer — modal-ready form used to leave a review for a completed
 * booking. Required: 1-5 star rating + optional comment. Optional expansion
 * reveals 7 axis sub-ratings AND audio/video testimonial uploaders.
 *
 * Props:
 *   - booking (the booking object — used to extract providerId + bookingId)
 *   - onSubmitted (fired after successful submit; parent should close modal & refetch)
 */
const AXES = [
  { key: "accuracy", label: "Accuracy" },
  { key: "professionalism", label: "Professionalism" },
  { key: "communication", label: "Communication" },
  { key: "insight", label: "Spiritual Insight" },
  { key: "followThrough", label: "Follow-Through" },
  { key: "ethics", label: "Ethics" },
  { key: "speed", label: "Speed of Delivery" },
];

export default function ReviewComposer({ booking, onSubmitted }) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [expand, setExpand] = useState(false);
  const [axes, setAxes] = useState({});
  const [testimonialKind, setTestimonialKind] = useState(null);
  const [testimonialUrl, setTestimonialUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const setAxis = (key, val) => setAxes((a) => ({ ...a, [key]: val }));

  const submit = async () => {
    if (!rating) {
      toast.error("Please tap a star rating");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews", {
        providerId: booking.providerId,
        bookingId: booking.id,
        rating,
        comment: comment.trim() || undefined,
        axes: Object.keys(axes).length ? axes : undefined,
        testimonialKind: testimonialKind || undefined,
        testimonialUrl: testimonialUrl || undefined,
      });
      toast.success("Review posted — +50 CAUL");
      onSubmitted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to post review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="review-composer">
      <div>
        <div className="label-mystic mb-2">Your rating</div>
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onMouseEnter={() => setHover(n)}
              onMouseLeave={() => setHover(0)}
              onClick={() => setRating(n)}
              className="p-1"
              data-testid={`review-star-${n}`}
            >
              <Star
                size={32}
                strokeWidth={1.25}
                className={
                  (hover || rating) >= n
                    ? "fill-secondary text-secondary"
                    : "text-foreground/30"
                }
              />
            </button>
          ))}
        </div>
      </div>

      <textarea
        rows={4}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="What stood out? How did the reading land?"
        className="w-full bg-input border border-secondary/20 px-3 py-2 rounded-sm focus:outline-none focus:border-secondary text-sm"
        data-testid="review-comment"
      />

      <button
        type="button"
        onClick={() => setExpand((v) => !v)}
        className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.16em] text-foreground/55 hover:text-secondary"
        data-testid="review-expand-toggle"
      >
        {expand ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        Rate 7 dimensions (optional) · Add audio/video testimonial
      </button>

      {expand && (
        <div className="space-y-5 border border-secondary/15 rounded-sm p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
            {AXES.map(({ key, label }) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-xs text-foreground/75">{label}</span>
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setAxis(key, n)}
                      data-testid={`axis-${key}-${n}`}
                      className="p-0.5"
                    >
                      <Star
                        size={14}
                        strokeWidth={1.25}
                        className={
                          (axes[key] || 0) >= n
                            ? "fill-secondary text-secondary"
                            : "text-foreground/25"
                        }
                      />
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <div className="label-mystic">Testimonial media (optional)</div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => { setTestimonialKind("text"); setTestimonialUrl(null); }}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs ${testimonialKind === "text" ? "border-secondary text-secondary" : "border-secondary/25 text-foreground/65"}`}
                data-testid="testimonial-kind-text"
              >
                <FileText size={12} /> Text only
              </button>
              <button
                type="button"
                onClick={() => setTestimonialKind("audio")}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs ${testimonialKind === "audio" ? "border-secondary text-secondary" : "border-secondary/25 text-foreground/65"}`}
                data-testid="testimonial-kind-audio"
              >
                <Mic size={12} /> Audio
              </button>
              <button
                type="button"
                onClick={() => setTestimonialKind("video")}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs ${testimonialKind === "video" ? "border-secondary text-secondary" : "border-secondary/25 text-foreground/65"}`}
                data-testid="testimonial-kind-video"
              >
                <Video size={12} /> Video
              </button>
            </div>
            {(testimonialKind === "audio" || testimonialKind === "video") && (
              <ImageUploader
                kind={testimonialKind === "video" ? "video" : "image"}
                value={testimonialUrl}
                onChange={setTestimonialUrl}
                accept={testimonialKind === "audio" ? "audio/*" : undefined}
                label={`Upload ${testimonialKind} testimonial`}
                testid="testimonial-uploader"
              />
            )}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={submit}
        disabled={submitting || !rating}
        className="bg-primary text-primary-foreground px-6 py-2 rounded-sm uppercase tracking-wider text-xs disabled:opacity-50"
        data-testid="review-submit"
      >
        {submitting ? "Posting..." : "Post Review"}
      </button>
    </div>
  );
}
